<template>
  <div class="frisbuy-mobile-media-slider">
    <div
      ref="track"
      class="frisbuy-mobile-media-slider__track"
    >
      <button
        v-for="(media, idx) in props.mediaList"
        :key="idx"
        type="button"
        class="frisbuy-mobile-media-slider__item"
        :class="{
          'frisbuy-mobile-media-slider__item--selected':
            props.selectedMediaThumbnailUrl &&
            media.thumbnailUrl === props.selectedMediaThumbnailUrl,
        }"
        @click="emit('selectMedia', media)"
      >
        <img
          class="frisbuy-mobile-media-slider__image"
          :src="DomainSubstitute(media.thumbnailUrl)"
          @error="errorImageHandler"
        />

        <span
          v-if="media.videoUrl.length > 0"
          class="frisbuy-mobile-media-slider__play-icon"
        >
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="16" fill="#212121" fill-opacity="0.2"/>
            <g opacity="0.8">
              <path
                d="M28.1192 22.7772C28.8701 23.2446 28.8701 24.4239 28.1192 24.8912L22.6451 28.2979C21.8999 28.7616 20.9727 28.1756 20.9727 27.2409L20.9727 20.4276C20.9727 19.4928 21.8999 18.9068 22.6451 19.3706L28.1192 22.7772Z"
                fill="white"
              />
              <path d="M34.125 24.0005C34.125 18.4083 29.5921 13.8744 24 13.8742C18.4076 13.8742 13.8737 18.4081 13.8737 24.0005C13.8739 29.5926 18.4078 34.1255 24 34.1255C29.592 34.1252 34.1248 29.5925 34.125 24.0005ZM36.7917 24.0005C36.7914 31.0652 31.0647 36.7919 24 36.7922C16.935 36.7922 11.2073 31.0654 11.207 24.0005C11.207 16.9354 16.9349 11.2075 24 11.2075C31.0649 11.2078 36.7917 16.9355 36.7917 24.0005Z" fill="white"/>
            </g>
          </svg>
        </span>
      </button>
    </div>

    <!-- Hidden hook to bootstrap jquery+fotorama from CDN within the component -->
    <div ref="fotoramaHost" class="frisbuy-mobile-media-slider__fotorama-host" aria-hidden="true"></div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import widgetTypes from "@/common/consts/widgetTypes";
import { imageWithPostId } from "../interfaces";

type JQ = ((...args: any[]) => any) & { fn?: Record<string, any> };

declare global {
  interface Window {
    jQuery?: JQ;
    $?: JQ;
  }
}

const props = defineProps<{
  mediaList: Array<imageWithPostId>;
  selectedMediaThumbnailUrl?: string;
}>();

const emit = defineEmits<{
  (e: "selectMedia", media: imageWithPostId): void;
  (e: "requestToLoadCustomersMedia"): void;
}>();

const track = ref<HTMLElement | null>(null);
const fotoramaHost = ref<HTMLElement | null>(null);
const PRELOAD_MEDIA_BEFORE_END = 2;
const SCROLL_END_THRESHOLD_PX = 8;
const didRequestAtScrollEnd = ref(false);

function errorImageHandler(e: Event) {
  MediaCatcher(e, 2, widgetTypes.STORY);
}

function ensureStylesheet(href: string) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function ensureScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function initFotoramaCdn() {
  try {
    ensureStylesheet("https://cdnjs.cloudflare.com/ajax/libs/fotorama/4.6.4/fotorama.css");
    await ensureScript("https://code.jquery.com/jquery-3.7.1.min.js");
    await ensureScript("https://cdnjs.cloudflare.com/ajax/libs/fotorama/4.6.4/fotorama.min.js");

    const $ = window.jQuery || window.$;
    if (!$ || !$.fn?.fotorama || !fotoramaHost.value) {
      return;
    }

    // Minimal hidden init so CDN dependency is bound to the component lifecycle.
    fotoramaHost.value.innerHTML = '<div class="fotorama" data-width="1" data-height="1" data-nav="false"></div>';
    $(fotoramaHost.value.querySelector('.fotorama')).fotorama();
  } catch (e) {
    // Soft-fail: slider keeps working with native touch scroll.
  }
}

function centerSelectedThumb() {
  if (!track.value || !props.selectedMediaThumbnailUrl) return;

  const selectedIndex = props.mediaList.findIndex(
    (media) => media.thumbnailUrl === props.selectedMediaThumbnailUrl
  );

  if (selectedIndex < 3) {
    track.value.scrollTo({
      left: 0,
      behavior: "smooth",
    });
    return;
  }

  const selectedEl = track.value.children[selectedIndex] as HTMLElement | undefined;
  if (!selectedEl) return;
  selectedEl.scrollIntoView({
    behavior: "smooth",
    inline: "center",
    block: "nearest",
  });
}

function requestToLoadCustomersMedia() {
  emit("requestToLoadCustomersMedia");
}

function shouldPreloadBySelectedMedia() {
  if (!props.selectedMediaThumbnailUrl || props.mediaList.length === 0) {
    return false;
  }

  const selectedIndex = props.mediaList.findIndex(
    (media) => media.thumbnailUrl === props.selectedMediaThumbnailUrl
  );

  if (selectedIndex < 0) {
    return false;
  }

  return selectedIndex >= props.mediaList.length - 1 - PRELOAD_MEDIA_BEFORE_END;
}

function checkNeedLoadBySelectedMedia() {
  if (shouldPreloadBySelectedMedia()) {
    requestToLoadCustomersMedia();
  }
}

function isTrackScrolledToEnd() {
  if (!track.value) {
    return false;
  }

  return (
    track.value.scrollLeft + track.value.clientWidth >=
    track.value.scrollWidth - SCROLL_END_THRESHOLD_PX
  );
}

function onTrackScroll() {
  const isAtEnd = isTrackScrolledToEnd();

  if (isAtEnd && !didRequestAtScrollEnd.value) {
    didRequestAtScrollEnd.value = true;
    requestToLoadCustomersMedia();
    return;
  }

  if (!isAtEnd) {
    didRequestAtScrollEnd.value = false;
  }
}

onMounted(async () => {
  await initFotoramaCdn();
  nextTick(centerSelectedThumb);
  track.value?.addEventListener("scroll", onTrackScroll, { passive: true });
  checkNeedLoadBySelectedMedia();
});

onBeforeUnmount(() => {
  track.value?.removeEventListener("scroll", onTrackScroll);
});

watch(
  () => props.selectedMediaThumbnailUrl,
  () => {
    nextTick(centerSelectedThumb);
    checkNeedLoadBySelectedMedia();
  }
);

watch(
  () => props.mediaList,
  () => {
    checkNeedLoadBySelectedMedia();
  },
  { deep: true }
);
</script>

<style scoped lang="scss">
.frisbuy-mobile-media-slider {
  width: 100%;
}

.frisbuy-mobile-media-slider__track {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  background: rgba(33, 33, 33, 1);
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 20px 28px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.frisbuy-mobile-media-slider__item {
  position: relative;
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.frisbuy-mobile-media-slider__item--selected::after {
  content: "";
  position: absolute;
  inset: -4px;
  border: 1px solid #ffffff;
  border-radius: 12px;
  pointer-events: none;
}

.frisbuy-mobile-media-slider__image {
  display: block;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
}

.frisbuy-mobile-media-slider__play-icon {
  position: absolute;
  inset: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: auto;
  z-index: 1;
  pointer-events: none;

  svg {
    width: 48px;
    height: 48px;
  }
}

.frisbuy-mobile-media-slider__fotorama-host {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
}
</style>
